import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const OnboardWizard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const companyId = user?.company_id;
  const [step, setStep] = useState(1);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitedName, setInvitedName] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role_ids: [],
  });

  useEffect(() => {
    if (companyId) loadRoles();
  }, [companyId]);

  const loadRoles = async () => {
    try {
      const res = await teamAPI.getRoles(companyId);
      setRoles(res.roles || []);
    } catch (err) {
      setError('Failed to load roles');
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter(id => id !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const canProceedStep1 = formData.email.trim() && formData.first_name.trim() && formData.last_name.trim();
  const canProceedStep2 = formData.role_ids.length > 0;

  const handleInvite = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await teamAPI.inviteUser(companyId, {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role_ids: formData.role_ids,
      });
      setInvitedName(`${formData.first_name} ${formData.last_name}`);
      setSuccess(true);
    } catch (err) {
      setError(err.error || err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center py-12 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <span className="text-3xl">✅</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invitation sent</h2>
          <p className="text-gray-600 mb-2">
            {invitedName} has been invited. They will receive an email with instructions to set up their account.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            They will have access to applications based on the roles you assigned.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => { setSuccess(false); setStep(1); setFormData({ email: '', first_name: '', last_name: '', role_ids: [] }); }}
              className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover text-sm font-medium"
            >
              Invite another
            </button>
            <button
              onClick={() => navigate('/tasks')}
              className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Back to team tasks
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate('/tasks')} className="text-gray-600 text-sm mb-6 hover:text-gray-900">
          ← {step > 1 ? 'Back' : 'Back to team tasks'}
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Invite a team member</h1>

        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-primary text-white' :
                s === step ? 'bg-primary text-white ring-4 ring-primary-light' :
                'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? '✓' : s}
              </div>
              <span className={`text-sm ${s <= step ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                {s === 1 ? 'Details' : s === 2 ? 'Access' : 'Review'}
              </span>
              {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-primary' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {/* Step 1: Who are you inviting? */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Who are you inviting?</h2>
            <p className="text-sm text-gray-500 mb-6">Enter their details to send an invitation email.</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="person@company.com"
                  className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="First name"
                    className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Last name"
                    className="w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary text-sm"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 2: What can they access? */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">What can they access?</h2>
            <p className="text-sm text-gray-500 mb-6">Select the roles that determine which applications they can use.</p>
            <div className="space-y-2">
              {roles.filter(r => r.is_active !== false).map(role => (
                <label
                  key={role.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    formData.role_ids.includes(role.id)
                      ? 'border-primary bg-primary-light/10'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.role_ids.includes(role.id)}
                    onChange={() => toggleRole(role.id)}
                    className="mt-0.5 rounded text-primary focus:ring-primary"
                  />
                  <div>
                    <div className="font-medium text-gray-900 text-sm">{role.name}</div>
                    {role.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{role.description}</div>
                    )}
                    {role.product && (
                      <span className="inline-block mt-1 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        Service: {role.product}
                      </span>
                    )}
                  </div>
                </label>
              ))}
              {roles.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No roles available.</p>
              )}
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(1)} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Review and send */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Review and send</h2>
            <p className="text-sm text-gray-500 mb-6">Check the details before sending the invitation.</p>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Person</h3>
                <p className="text-sm text-gray-900">
                  {formData.first_name} {formData.last_name}
                </p>
                <p className="text-sm text-gray-500">{formData.email}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Roles ({formData.role_ids.length})
                </h3>
                {formData.role_ids.length === 0 ? (
                  <p className="text-sm text-red-500">No roles selected — they won't be able to access any applications.</p>
                ) : (
                  <div className="space-y-1.5">
                    {formData.role_ids.map(rid => {
                      const role = roles.find(r => r.id === rid);
                      return role ? (
                        <div key={rid} className="flex items-center gap-2 text-sm">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span className="text-gray-900">{role.name}</span>
                          {role.description && (
                            <span className="text-xs text-gray-400">— {role.description}</span>
                          )}
                        </div>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(2)} className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
                Back
              </button>
              <button
                onClick={handleInvite}
                disabled={loading || formData.role_ids.length === 0}
                className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 text-sm font-medium"
              >
                {loading ? 'Sending...' : 'Send invitation'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardWizard;
